import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { setSelectedIds } from "../state/TemplateCustomersSlice";
import { bindAction } from "../../../../interfaces";
import { getCustomers } from "../state/thunks/TemplateCustomersThunk";
import { GroupTemplateCustomers } from "../Details/GroupTemplateCustomers";
import { RouteNames, SortingDirection } from "../../../../enums";
import { Button } from "components/buttons/button/Button";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import AddCustomersModal from "./Customers/AddCustomersModal";
import { Columns, editColumnOptions, editColumnToParamMap } from "../Details/Customers/getColumnOptions";
import { Observable } from "utils";
import EditCustomerPacksModal from "./Customers/EditCustomerPacksModal";
import { useNavigate } from "react-router";
import { fetchAccounts } from "features/Accounts/AllAccountsOverview/state/thunks/allAccountsOverviewThunk";
import styles from "./editTemplateCustomers.module.scss";

type Props = PropsFromRedux & {
  templateId: number;
  handleRemoveClick: (ids: number[]) => void;
  disabled?: boolean;
  setCustomersTabActionButton?: (actionButton: ReactNode) => void;
};

export const EditTemplateCustomers = (props: Props) => {
  const {
    items,
    moboAccountId,
    isLoading,
    itemsCount,
    getData,
    templateId,
    handleRemoveClick,
    setSelectedIds,
    selectedIds,
    disabled,
    handleFetchAccounts,
    accountsTotalCount,
    accountsError,
    setCustomersTabActionButton,
  } = props;
  const [customersModalShown, setCustomersModalShown] = useState(false);
  const onTriggerEditLicensingObserver = useMemo(() => new Observable(), []);
  const navigate = useNavigate();

  useEffect(() => {
    handleFetchAccounts({ skip: 0, take: 1 });
  }, [handleFetchAccounts]);

  const loadCustomers = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = Columns.Added.toLowerCase(),
    sortingDirection: SortingDirection = SortingDirection.Descending,
    templateId?: number,
    searchTerm?: string,
  ) => {
    getData(templateId!, skip, top, sortingColumnName, sortingDirection, searchTerm);
  };

  // If the request fails, show the "add customers to template" button
  const hasChildCustomers = !!accountsError || accountsTotalCount > 0;

  const renderAddCustomersButton = useCallback(() => {
    if (hasChildCustomers) {
      return (
        <Button
          primary
          content={"Add Customers"}
          className="add-customers-button create-button"
          onClick={() => setCustomersModalShown(true)}
        />
      );
    }
    return (
      <Button
        primary
        className={styles["create-customer-button"]}
        onClick={() => navigate(`/${RouteNames.linkedAccountsManagement}/create`)}
      >
        Create Customer
      </Button>
    );
  }, [hasChildCustomers, navigate]);

  useEffect(() => {
    setCustomersTabActionButton?.(renderAddCustomersButton());
  }, [renderAddCustomersButton, setCustomersTabActionButton]);

  const handleEditLicensingClick = (customerId: number) => {
    onTriggerEditLicensingObserver.notify(templateId, customerId);
  };

  return (
    <>
      <GroupTemplateCustomers
        getData={loadCustomers}
        items={items}
        templateId={templateId}
        moboAccountId={moboAccountId}
        renderAddCustomersButton={renderAddCustomersButton}
        hasChildCustomers={hasChildCustomers}
        isLoading={isLoading}
        itemsCount={itemsCount}
        setSelectedIds={setSelectedIds}
        columnOptions={editColumnOptions({
          onEditLicensingClick: (id) => handleEditLicensingClick(id),
          onRemoveClick: (id) => handleRemoveClick([id]),
          disabled,
        })}
        selectedIds={selectedIds}
        disabled={disabled}
        columnMap={editColumnToParamMap}
      />
      <AddCustomersModal
        showModal={customersModalShown}
        setShowModal={setCustomersModalShown}
        templateId={templateId}
      />
      <EditCustomerPacksModal onTriggerModalObserver={onTriggerEditLicensingObserver} />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    items: state.people.groupTemplate.customers.items,
    itemsCount: state.people.groupTemplate.customers.itemsCount,
    isLoading: state.people.groupTemplate.customers.isLoading,
    selectedIds: state.people.groupTemplate.customers.selectedIds,
    accountsTotalCount: state.accounts.overview.grid.itemsCount,
    accountsError: state.accounts.overview.grid.error,
    moboAccountId: state.accounts.edit.accountInfo.accountConfiguration.id,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    getData: bindAction(getCustomers, dispatch),
    setSelectedIds: bindAction(setSelectedIds, dispatch),
    handleFetchAccounts: bindAction(fetchAccounts, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditTemplateCustomers);
export default ConnectedComponent;
